import React, { useState } from 'react'
import { Alert, Empty, Input, Modal, Skeleton, Tooltip } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { Robot, Laptop } from '@phosphor-icons/react'
import dayjs from 'dayjs'
import {
  useGenerateMcpToken,
  useMcpConnectionInfo,
  useMcpTokens,
  useRevokeMcpToken,
} from 'api/McpToken/McpToken'
import { McpTokenRow } from 'types/mcpToken/mcpToken'
import { successMessage } from 'api/global'

/** The app's primary, from `$main` in `assets/variables.scss`. */
const MAIN = '#2BA7CA'

/**
 * AI Assistants — one screen covering both ways to connect an assistant to Bamrec.
 *
 * ── WHY ONE SCREEN AND NOT A "CONNECT" BUTTON ──────────────────────────────────────────────────
 *
 * There are two paths, but only one screen is needed, because in the OAuth path **the user does not
 * start here**. They start inside ChatGPT or Claude, paste our URL, and get sent to the consent page
 * to sign in and approve. A "Connect with AI" button in this app could not begin that flow — there
 * is nothing for it to launch.
 *
 * So this screen has exactly three jobs: hand over the URL, mint a token for terminal tools, and
 * show what currently has access so it can be taken away. The third did not exist before — nobody
 * could see which assistants could read their data.
 *
 * Built from the app's own classes — `card-table`, `prim-sm-btn`, `borderd-btn`, `btn-gray`,
 * `btn-delete`, `pra-heading`, `gary-text` — so it inherits design-system changes like every other
 * screen. Card padding is inline because this app has no spacing utilities (`px-30` and friends do
 * not exist); only the colours and components are shared.
 */
const AiAssistants = () => {
  const { data: connection, isLoading: connectionLoading } = useMcpConnectionInfo()
  const { data: tokens, isLoading: tokensLoading } = useMcpTokens()
  const generate = useGenerateMcpToken()
  const revoke = useRevokeMcpToken()

  const [label, setLabel] = useState('')
  const [showGenerate, setShowGenerate] = useState(false)
  // Held only while the modal is open. The API returns the credential once and never again, so it is
  // deliberately not cached, refetched or persisted.
  const [mintedToken, setMintedToken] = useState<string | null>(null)
  const [showPast, setShowPast] = useState(false)

  // Split rather than sorted. What has access RIGHT NOW is the question this section answers; on an
  // account with history the past rows run to dozens and would bury it entirely.
  const active = (tokens ?? []).filter(t => t.isActive)
  const past = (tokens ?? []).filter(t => !t.isActive)

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value)
      successMessage(`${what} copied`)
    } catch {
      // Clipboard access can be refused (insecure origin, permissions). The value is on screen and
      // selectable, so this is a convenience failing, not the task.
      successMessage(`Select and copy the ${what.toLowerCase()} manually`)
    }
  }

  const onGenerate = async () => {
    const created = await generate.mutateAsync({ label: label.trim() || undefined })
    setMintedToken(created?.token ?? null)
    setLabel('')
  }

  const closeGenerate = () => {
    setShowGenerate(false)
    // Cleared on close — it cannot be shown again, and holding it serves nothing.
    setMintedToken(null)
  }

  return (
    <>
      <h1 className="general-s-heading">AI Assistants</h1>

      <div className="card-table" style={{ padding: '24px 30px', marginBottom: 20 }}>
        <p className="pra-heading">Use with ChatGPT or Claude</p>
        <p className="gary-text" style={{ fontSize: 14, marginBottom: 16 }}>
          Add Bamrec as a connector in your assistant and paste this address. You will be asked to
          sign in and choose what it can read.
        </p>

        {connectionLoading ? (
          <Skeleton.Input active block style={{ height: 40 }} />
        ) : connection?.available && connection.mcpServerUrl ? (
          <Input
            readOnly
            value={connection.mcpServerUrl}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
            addonAfter={
              <Tooltip title="Copy address">
                <CopyOutlined
                  style={{ cursor: 'pointer', color: MAIN }}
                  onClick={() => copy(connection.mcpServerUrl as string, 'Address')}
                />
              </Tooltip>
            }
          />
        ) : (
          // No URL is offered when the surface is switched off — an address that refuses every
          // connection is worse than an explanation.
          <Alert
            type="info"
            showIcon
            message="Not available in this environment yet"
            description={connection?.reason ?? 'AI assistant connections are not switched on here.'}
          />
        )}
      </div>

      <div className="card-table" style={{ padding: '24px 30px', marginBottom: 20 }}>
        <p className="pra-heading">Use with a terminal tool</p>
        <p className="gary-text" style={{ fontSize: 14, marginBottom: 16 }}>
          Tools that cannot sign in through a browser need a token you paste in yourself. It is shown
          once and expires after {connection?.maxTokenLifetimeDays ?? 30} days.
        </p>
        <button
          className={connection?.available ? 'prim-sm-btn' : 'btn-disabled'}
          disabled={!connection?.available}
          onClick={() => setShowGenerate(true)}
        >
          Generate token
        </button>
      </div>

      <div className="card-table" style={{ padding: '24px 30px' }}>
        <p className="pra-heading">Connected assistants</p>
        <p className="gary-text" style={{ fontSize: 14, marginBottom: 16 }}>
          Everything that can currently read your Bamrec data. Removing one takes effect immediately.
        </p>

        {tokensLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : active.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="gary-text" style={{ fontSize: 14 }}>
                Nothing has access right now.
              </span>
            }
            style={{ margin: '16px 0' }}
          />
        ) : (
          active.map((row: McpTokenRow, index: number) => (
            <ConnectionRow
              key={row.tokenId}
              row={row}
              isFirst={index === 0}
              onRevoke={() => revoke.mutate(row.tokenId)}
              revoking={revoke.isLoading}
            />
          ))
        )}

        {/* Past connections are collapsed, not dropped. Someone whose assistant stopped working needs
            to find out that it was removed or expired — but inline they bury the live ones. */}
        {past.length > 0 && (
          <>
            <button
              className="btn-borderless"
              style={{ color: MAIN, fontWeight: 500, paddingLeft: 0, marginTop: 8 }}
              onClick={() => setShowPast(!showPast)}
            >
              {showPast ? 'Hide' : 'Show'} {past.length} past connection
              {past.length === 1 ? '' : 's'}
            </button>

            {showPast &&
              past.map((row: McpTokenRow, index: number) => (
                <ConnectionRow
                  key={row.tokenId}
                  row={row}
                  isFirst={index === 0}
                  onRevoke={() => revoke.mutate(row.tokenId)}
                  revoking={revoke.isLoading}
                />
              ))}
          </>
        )}
      </div>

      <Modal
        open={showGenerate}
        onCancel={closeGenerate}
        title={mintedToken ? 'Your token' : 'Generate a token'}
        footer={null}
      >
        {mintedToken ? (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Copy this now"
              description="It is shown once and cannot be recovered. If you lose it, generate another."
            />
            <Input.TextArea
              readOnly
              value={mintedToken}
              autoSize
              style={{ fontFamily: 'monospace', fontSize: 13, marginBottom: 20 }}
            />
            <div className="w-100 flex justify-end" style={{ gap: 10 }}>
              <button className="borderd-btn" onClick={() => copy(mintedToken, 'Token')}>
                Copy token
              </button>
              <button className="prim-sm-btn" onClick={closeGenerate}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="gary-text" style={{ fontSize: 14, marginBottom: 12 }}>
              Give it a name so you can recognise it later — the device or tool you will use it on.
            </p>
            <Input
              placeholder="My laptop"
              value={label}
              maxLength={60}
              onChange={e => setLabel(e.target.value)}
              onPressEnter={onGenerate}
              style={{ marginBottom: 20 }}
            />
            <div className="w-100 flex justify-end" style={{ gap: 10 }}>
              <button className="btn-gray" onClick={closeGenerate}>
                Cancel
              </button>
              <button
                className="prim-sm-btn"
                disabled={generate.isLoading}
                onClick={onGenerate}
              >
                {generate.isLoading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}

/**
 * A small, quiet label. Grey by default; `strong` marks the one value worth noticing.
 *
 * Deliberately not antd's `<Tag>`: its coloured variants are built for status (success, error) and
 * on this row they made a scope label look like a warning.
 */
const pill = (strong: boolean): React.CSSProperties => ({
	display: 'inline-block',
	fontSize: 11,
	fontWeight: 500,
	lineHeight: '18px',
	padding: '0 8px',
	borderRadius: 4,
	background: strong ? '#fdecea' : '#f2f4f6',
	color: strong ? '#a8322a' : '#6b7280',
	whiteSpace: 'nowrap',
})

/**
 * One connection.
 *
 * A connector and a pasted token are told apart by `issuedVia` — the reason the API returns it. They
 * are different things: one is a browser assistant that signed itself in, the other a string someone
 * pasted into a terminal.
 */
const ConnectionRow = ({
  row,
  isFirst,
  onRevoke,
  revoking,
}: {
  row: McpTokenRow
  isFirst: boolean
  onRevoke: () => void
  revoking: boolean
}) => {
  const isConnector = row.issuedVia === 'oauth'
  const inactive = !row.isActive

  const scopeLabel: Record<string, string> = {
    'events:read': 'events',
    'families:read': 'families',
    'leagues:read': 'leagues',
  }

  /**
   * What this connection can see, in the words the person approving it would use.
   *
   * `subjectType` is a raw enum — "organizer", "family" — and on its own it tells a reader nothing
   * about REACH, which is the thing that actually differs between rows: an admin connection reads
   * every organization while an organizer one reads a single organization. That difference is the
   * reason to keep or remove a connection, so it belongs in the sentence rather than behind a word
   * only the backend uses.
   */
  /** Short form for the tag; `subjectLabel` below is the longer phrase used in the detail line. */
  const roleTag: Record<string, string> = {
    organizer: 'Organizer',
    admin: 'Admin',
    guardian: 'Parent',
    family: 'Family',
  }

  const subjectLabel: Record<string, string> = {
    organizer: 'your organization',
    admin: 'all organizations',
    guardian: 'your family',
    family: 'your family',
  }

  const reads = row.scopes?.length
    ? row.scopes.map(s => scopeLabel[s] ?? s).join(', ')
    : 'nothing'
  const scope = subjectLabel[row.subjectType] ?? row.subjectType

  /** "used 28 Aug" is ambiguous once a year has passed; "never used" is a real and useful state. */
  const lastUsed = row.lastUsedAt
    ? `Last used ${dayjs(row.lastUsedAt).format('D MMM YYYY')}`
    : 'Never used'

  const status = inactive
    ? row.revokedAt
      ? `Removed ${dayjs(row.revokedAt).format('D MMM YYYY')}`
      : `Expired ${row.expiresAt ? dayjs(row.expiresAt).format('D MMM YYYY') : ''}`.trim()
    : row.expiresAt
      // "Reconnect by" for a browser connector: nothing is lost when the window closes, the user
      // just approves it again. "Expires" is right for a pasted token, which genuinely stops.
      ? `${isConnector ? 'Reconnect by' : 'Expires'} ${dayjs(row.expiresAt).format('D MMM YYYY')}`
      : ''

  const confirmRevoke = () => {
    Modal.confirm({
      title: `Remove ${row.label || 'this connection'}?`,
      content:
        'It will stop being able to read your Bamrec data straight away. To reconnect, it will have to be set up again.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: onRevoke,
    })
  }

  return (
    <div
      className="flex"
      style={{
        // Top-aligned: the row is now several lines tall, and centring pushes the icon adrift.
        alignItems: 'flex-start',
        gap: 14,
        padding: '16px 0',
        borderTop: isFirst ? 'none' : '1px solid #ebedf1',
        opacity: inactive ? 0.6 : 1,
      }}
    >
      {isConnector ? <Robot size={22} color={MAIN} /> : <Laptop size={22} color="#939598" />}

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Line 1 — what this is. */}
        <div className="flex align-center" style={{ gap: 10, flexWrap: 'wrap' }}>
          {/* NOT `card-label-text` — that class is a bordered chip (7px padding, 1px border), so it
              drew a box round the name that read as a text input. Plain type, sized to lead the row. */}
          <span style={{ fontSize: 15, fontWeight: 600, color: '#000229' }}>
            {row.label || (isConnector ? 'AI assistant' : 'Token')}
          </span>

          {/* ── ONE QUIET TAG STYLE ──────────────────────────────────────────────────────────────
              Three loud antd tags in a row competed with the name and with each other, and a red
              "Admin" read as an error rather than as scope. These are borderless grey pills: the
              row's job is to be scannable, and the words carry the meaning without colour shouting.
              Admin alone is emphasised — it is the one value that means platform-wide. */}
          <span style={pill(row.subjectType === 'admin')}>
            {roleTag[row.subjectType] ?? row.subjectType}
          </span>
          <span style={pill(false)}>{isConnector ? 'Browser' : 'Terminal'}</span>
          {inactive && <span style={pill(false)}>{row.revokedAt ? 'Removed' : 'Expired'}</span>}
        </div>

        {/* ── LABELLED FIELDS, NOT A RUN-ON LINE ────────────────────────────────────────────────
            Every value here answers a different question — what it can read, how far that reaches,
            whether it is still being used. Strung together with dots they read as one unlabelled
            string ("organizer · Events, Families · used 28 Aug") and a person has to guess which
            part means what. Each gets its own label. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
            marginTop: 8,
            fontSize: 13,
          }}
        >
          <span className="gary-text" style={{ fontSize: 12 }}>
            Can read
          </span>
          <span>{reads}</span>

          <span className="gary-text" style={{ fontSize: 12 }}>
            Across
          </span>
          <span>{scope}</span>

          <span className="gary-text" style={{ fontSize: 12 }}>
            {inactive ? 'Status' : 'Activity'}
          </span>
          <span>
            {lastUsed}
            {status ? ` · ${status}` : ''}
          </span>
        </div>
      </div>

      {!inactive && (
        <button
          className="btn-borderless"
          // Text, not an outlined block. Remove is a rare, deliberate action — it should be findable,
          // not the loudest thing in the row.
          style={{ alignSelf: 'flex-start', color: '#a8322a', fontWeight: 500, fontSize: 13 }}
          onClick={confirmRevoke}
          disabled={revoking}
        >
          Remove
        </button>
      )}
    </div>
  )
}

export default AiAssistants
